import { saveData } from "../core/cloudSync.js";
import { genId, today } from "../core/utils.js";
import { render } from "../ui/app.js";

import { C, COMISIONES_KEY, DEFAULT_TABLA_COMISIONES } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, inputStyle, parseNumber } from "../core/utils.js";
import { getLiquidacionOP } from "./documentos.js";

export function getComisionEntradaOP(op) {
  return (op._conceptosCosto || []).find(c => c._esComision === true) || null;
}

export function getTablaComisiones() {
  try {
    const raw = localStorage.getItem(COMISIONES_KEY);
    return raw ? JSON.parse(raw) : JSON.parse(JSON.stringify(DEFAULT_TABLA_COMISIONES));
  } catch { return JSON.parse(JSON.stringify(DEFAULT_TABLA_COMISIONES)); }
}

export function saveTablaComisiones(tabla) {
  try { localStorage.setItem(COMISIONES_KEY, JSON.stringify(tabla)); } catch {}
}

export function calcComisionPct(margenPct, rangoVendedorId) {
  const tabla = getTablaComisiones();
  const rango = rangoVendedorId || "general";
  // buscar reglas del rango, o fallback a general
  let reglas = tabla.reglas.filter(r => r.rangoVendedor === rango);
  if (reglas.length === 0) reglas = tabla.reglas.filter(r => r.rangoVendedor === "general");
  for (const r of reglas) {
    const min = parseNumber(r.margenMin);
    const max = r.margenMax !== null && r.margenMax !== "" ? parseNumber(r.margenMax) : Infinity;
    if (margenPct >= min && margenPct <= max) return parseNumber(r.comisionPct);
  }
  return 0;
}

// Quién puede vender: el cargo "vendedor" O cualquier empleado marcado con
// es_vendedor — un gerente o administrativo que también genera ventas conserva
// su cargo y aun así entra en comisiones y en la asignación de clientes.
export function getVendedores() {
  return (state.data.empleados || []).filter(e => e.cargo === "vendedor" || e.es_vendedor);
}

export function liquidarComisiones(desde, hasta, vendedorFiltroId) {
  const vendedores = getVendedores();
  const vendedorFiltro = vendedores.find(v => v.id === vendedorFiltroId);

  const ops = (state.data.pedidos || []).filter(op => {
    if (op.estado === "cancelada") return false;
    if (vendedorFiltroId) {
      // Filtrar por nombre del vendedor seleccionado
      const nombreFiltro = vendedorFiltro ? vendedorFiltro.nombre : "";
      if ((op.asesor_comercial || "") !== nombreFiltro) return false;
    }
    if (desde && op.fecha < desde) return false;
    if (hasta && op.fecha > hasta) return false;
    return true;
  });

  return ops.map(op => {
    const liq = getLiquidacionOP(op.id) || { ventaTotal: parseNumber(op.total)||0, margen: 0, costoTotal: 0 };
    const ventaNeta = liq.ventaTotal || 0;
    const margen = liq.margen || 0;
    // Buscar vendedor por nombre (asesor_comercial)
    const vendedor = vendedores.find(v => v.nombre === (op.asesor_comercial || ""));
    const rangoVendedor = vendedor ? (vendedor.rango || "general") : "general";
    const comisionPct = calcComisionPct(margen, rangoVendedor);
    const comisionMonto = Math.round(ventaNeta * (comisionPct / 100));
    // Estado de aplicación de la comisión a los costos de la OP
    const entradaComision = getComisionEntradaOP(op);
    const yaAplicada = !!entradaComision;
    const montoYaAplicado = yaAplicada ? (parseNumber(entradaComision.valor_tercero) || 0) : 0;
    // Margen post-comisión:
    // Si ya está aplicada → el margen actual ya la incluye
    // Si no → simular deduciendo la comisión del costo total
    const costoTotal = liq.costoTotal || 0;
    const margenPost = yaAplicada
      ? margen
      : (ventaNeta > 0 ? ((ventaNeta - costoTotal - comisionMonto) / ventaNeta) * 100 : 0);
    return {
      opId: op.id,
      opNumero: op.numero || op.id,
      fecha: op.fecha || "",
      cliente: (state.data.clientes || []).find(c => c.id === op.cliente_id),
      vendedor,
      asesorNombre: op.asesor_comercial || "Sin asignar",
      rangoVendedor,
      ventaNeta,
      margen,
      comisionPct,
      comisionMonto,
      liqData: liq,
      yaAplicada,
      montoYaAplicado,
      margenPost
    };
  }).filter(r => r.ventaNeta > 0);
}

export function renderComisiones() {
  const tab = state.comisionesTab || "liquidacion";
  const f = state.comisionesFiltro || {};
  const vendedores = getVendedores();
  const tabla = getTablaComisiones();
  const amber = C.amber;

  const tabNav = `
    <div style="display:flex;gap:0;margin-bottom:10px;border-bottom:2px solid ${C.border};">
      ${[["liquidacion","📋 Liquidación de Comisiones"],["tabla","📐 Tabla & Rangos"]].map(([id,label]) => `
        <button data-action="comisiones-tab" data-tab="${id}" style="
          padding:10px 20px;background:none;border:none;
          border-bottom:${tab===id?`3px solid ${amber}`:"3px solid transparent"};
          font-weight:${tab===id?"800":"500"};
          color:${tab===id?amber:C.muted};cursor:pointer;font-size:12px;margin-bottom:-2px;">
          ${label}
        </button>
      `).join("")}
    </div>
  `;

  const contenido = tab === "liquidacion"
    ? renderLiquidacionComisiones(f, vendedores)
    : renderTablaComisionesEditor(tabla);

  return `
    <div style="padding:11px;max-width:1300px;">
      <div style="font-size:17px;font-weight:800;color:${C.text};margin-bottom:10px;">💼 Módulo de Comisiones</div>
      ${tabNav}
      ${contenido}
    </div>
  `;
}

export function renderLiquidacionComisiones(f, vendedores) {
  const amber = C.amber;
  const filas = liquidarComisiones(f.desde, f.hasta, f.vendedor);

  // Agrupar por vendedor para el resumen
  const porVendedor = {};
  filas.forEach(r => {
    const vid = r.vendedor ? r.vendedor.id : ("__" + r.asesorNombre);
    if (!porVendedor[vid]) porVendedor[vid] = { nombre: r.asesorNombre, rangoVendedor: r.rangoVendedor, ops: [], totalVenta: 0, totalComision: 0 };
    porVendedor[vid].ops.push(r);
    porVendedor[vid].totalVenta += r.ventaNeta;
    porVendedor[vid].totalComision += r.comisionMonto;
  });

  const totVenta = filas.reduce((s, r) => s + r.ventaNeta, 0);
  const totComision = filas.reduce((s, r) => s + r.comisionMonto, 0);

  const filtros = `
    <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px;align-items:flex-end;">
      <div>
        <label style="font-size:10.5px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">VENDEDOR</label>
        <select onchange="state.comisionesFiltro.vendedor=this.value;render();" style="${inputStyle}width:180px;">
          <option value="">— Todos —</option>
          ${vendedores.map(v => `<option value="${v.id}" ${f.vendedor===v.id?"selected":""}>${escapeHTML(v.nombre)}</option>`).join("")}
        </select>
      </div>
      <div>
        <label style="font-size:10.5px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">DESDE</label>
        <input type="date" onchange="state.comisionesFiltro.desde=this.value;render();" value="${f.desde||""}" style="${inputStyle}width:140px;"/>
      </div>
      <div>
        <label style="font-size:10.5px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">HASTA</label>
        <input type="date" onchange="state.comisionesFiltro.hasta=this.value;render();" value="${f.hasta||""}" style="${inputStyle}width:140px;"/>
      </div>
      <button data-action="comisiones-limpiar-filtros" style="${btnStyle(null,"secondary")}">Limpiar</button>
      <button onclick="exportarExcelComisiones()" style="background:#16a34a;color:white;border:none;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;align-self:flex-end;">📥 Exportar Excel</button>
    </div>
  `;

  // KPI cards
  const kpis = `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:10px;">
      ${[
        ["📦 OPs incluidas", filas.length, false],
        ["💰 Venta neta total", fmtCOP(totVenta), false],
        ["🏆 Total comisiones", fmtCOP(totComision), true]
      ].map(([lbl, val, highlight]) => `
        <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;${highlight?`border-left:4px solid ${amber};`:""}">
          <div style="font-size:11.5px;color:${C.muted};margin-bottom:4px;">${lbl}</div>
          <div style="font-size:17px;font-weight:800;color:${highlight?amber:C.text};">${val}</div>
        </div>
      `).join("")}
    </div>
  `;

  // Resumen por vendedor
  const resumen = Object.values(porVendedor).length === 0 ? "" : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;margin-bottom:10px;overflow:auto;">
      <div style="padding:12px 16px;border-bottom:1px solid ${C.border};font-weight:700;font-size:12px;color:${C.text};">📊 Resumen por vendedor</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f1f5f9;">
          <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;">VENDEDOR</th>
          <th style="padding:6px 9px;text-align:left;font-size:10.5px;font-weight:700;">RANGO</th>
          <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;"># OPs</th>
          <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;">VENTA NETA</th>
          <th style="padding:6px 9px;text-align:right;font-size:10.5px;font-weight:700;color:${amber};">COMISIÓN TOTAL</th>
        </tr></thead>
        <tbody>
          ${Object.values(porVendedor).map(g => `
            <tr style="border-top:1px solid ${C.border};">
              <td style="padding:6px 9px;font-weight:600;">${escapeHTML(g.nombre)}</td>
              <td style="padding:6px 9px;"><span style="background:${amber}20;color:${amber};padding:2px 8px;border-radius:6px;font-size:10.5px;font-weight:700;">${escapeHTML(g.rangoVendedor)}</span></td>
              <td style="padding:6px 9px;text-align:right;">${g.ops.length}</td>
              <td style="padding:6px 9px;text-align:right;">${fmtCOP(g.totalVenta)}</td>
              <td style="padding:6px 9px;text-align:right;font-weight:800;color:${amber};">${fmtCOP(g.totalComision)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;

  // ── Borrador de impacto sobre liquidación de costos ─────────────────
  const conComision    = filas.filter(r => r.comisionMonto > 0);
  const pendientes     = conComision.filter(r => !r.yaAplicada);
  const yaAplicadas    = conComision.filter(r => r.yaAplicada);
  const totPendMonto   = pendientes.reduce((s, r) => s + r.comisionMonto, 0);
  const totAplicMonto  = yaAplicadas.reduce((s, r) => s + r.montoYaAplicado, 0);

  const margenColorFn = pct => pct >= 24.5 ? "#10b981" : pct >= 19.59 ? amber : pct >= 15 ? "#f59e0b" : "#ef4444";

  const borrador = conComision.length === 0 ? "" : `
    <div style="background:white;border:2px solid ${amber};border-radius:6px;margin-bottom:10px;overflow:hidden;">
      <div style="padding:12px 18px;background:${amber}12;border-bottom:1px solid ${amber}40;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;">
        <div>
          <span style="font-size:12px;font-weight:800;color:${amber};">📊 Borrador — Impacto sobre Liquidación de Costos</span>
          <span style="margin-left:12px;font-size:11.5px;color:${C.muted};">Revisa el efecto antes de aplicar oficialmente la comisión a cada OP</span>
        </div>
        ${pendientes.length > 0 ? `
        <button data-action="aplicar-todas-comisiones" style="${btnStyle(amber)}">
          ✅ Aplicar todas las pendientes (${pendientes.length})
        </button>` : `<span style="color:#10b981;font-weight:700;font-size:12px;">✓ Todas aplicadas</span>`}
      </div>

      <!-- Resumen de estado -->
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:0;border-bottom:1px solid ${C.border};">
        ${[
          ["OPs con comisión", conComision.length, amber, ""],
          ["Pendientes de aplicar", pendientes.length, "#ef4444", fmtCOP(totPendMonto)],
          ["Ya aplicadas", yaAplicadas.length, "#10b981", fmtCOP(totAplicMonto)]
        ].map(([lbl, n, col, sub]) => `
          <div style="padding:14px 18px;border-right:1px solid ${C.border};">
            <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">${lbl}</div>
            <div style="font-size:16px;font-weight:800;color:${col};">${n}</div>
            ${sub ? `<div style="font-size:11.5px;color:${C.muted};margin-top:2px;">${sub}</div>` : ""}
          </div>
        `).join("")}
      </div>

      <!-- Tabla detalle por OP con impacto -->
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
          <thead><tr style="background:#f8f9fa;">
            <th style="padding:9px 10px;text-align:left;font-size:10.5px;font-weight:700;">OP</th>
            <th style="padding:9px 10px;text-align:left;font-size:10.5px;font-weight:700;">FECHA</th>
            <th style="padding:9px 10px;text-align:left;font-size:10.5px;font-weight:700;">CLIENTE</th>
            <th style="padding:9px 10px;text-align:left;font-size:10.5px;font-weight:700;">VENDEDOR</th>
            <th style="padding:9px 10px;text-align:right;font-size:10.5px;font-weight:700;">VENTA NETA</th>
            <th style="padding:9px 10px;text-align:right;font-size:10.5px;font-weight:700;">COSTO TOTAL</th>
            <th style="padding:9px 10px;text-align:right;font-size:10.5px;font-weight:700;">MARGEN ACTUAL</th>
            <th style="padding:9px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${amber};">COMISIÓN $</th>
            <th style="padding:9px 10px;text-align:right;font-size:10.5px;font-weight:700;">MARGEN POST-COM.</th>
            <th style="padding:9px 10px;text-align:center;font-size:10.5px;font-weight:700;">ESTADO</th>
            <th style="padding:9px 10px;text-align:center;font-size:10.5px;font-weight:700;">ACCIÓN</th>
          </tr></thead>
          <tbody>
            ${conComision.map(r => {
              const costoActual = r.liqData ? (r.liqData.costoTotal || 0) : 0;
              const mColor = margenColorFn(r.margen);
              const mpColor = margenColorFn(r.margenPost);
              const deltaMargen = r.yaAplicada ? 0 : (r.margenPost - r.margen);
              const fondo = r.yaAplicada ? "#f0fdf4" : "#fffbeb";
              return `
              <tr style="border-top:1px solid ${C.border};background:${fondo};">
                <td style="padding:8px 10px;font-weight:700;color:${C.mathiz};">${escapeHTML(r.opNumero)}</td>
                <td style="padding:8px 10px;">${fmtDate(r.fecha)}</td>
                <td style="padding:8px 10px;">${escapeHTML(r.cliente ? r.cliente.nombre : "—")}</td>
                <td style="padding:8px 10px;">
                  ${escapeHTML(r.asesorNombre)}
                  ${r.rangoVendedor !== "general" ? `<span style="margin-left:4px;background:${amber}20;color:${amber};padding:1px 5px;border-radius:6px;font-size:10px;">${r.rangoVendedor}</span>` : ""}
                </td>
                <td style="padding:8px 10px;text-align:right;">${fmtCOP(r.ventaNeta)}</td>
                <td style="padding:8px 10px;text-align:right;color:${C.muted};">${fmtCOP(costoActual)}</td>
                <td style="padding:8px 10px;text-align:right;font-weight:700;color:${mColor};">${r.margen.toFixed(2)}%</td>
                <td style="padding:8px 10px;text-align:right;font-weight:800;color:${amber};">${fmtCOP(r.comisionMonto)}</td>
                <td style="padding:8px 10px;text-align:right;">
                  <span style="font-weight:700;color:${mpColor};">${r.margenPost.toFixed(2)}%</span>
                  ${!r.yaAplicada && deltaMargen !== 0 ? `<span style="margin-left:4px;font-size:10px;color:${deltaMargen < 0 ? "#ef4444" : "#10b981"};">(${deltaMargen > 0 ? "+" : ""}${deltaMargen.toFixed(2)}%)</span>` : ""}
                </td>
                <td style="padding:8px 10px;text-align:center;">
                  ${r.yaAplicada
                    ? `<span style="background:#dcfce7;color:#15803d;padding:3px 10px;border-radius:20px;font-size:10.5px;font-weight:700;">✓ Aplicada</span>`
                    : `<span style="background:#fef9c3;color:#854d0e;padding:3px 10px;border-radius:20px;font-size:10.5px;font-weight:700;">⏳ Pendiente</span>`}
                </td>
                <td style="padding:8px 10px;text-align:center;">
                  ${r.yaAplicada
                    ? `<button data-action="reversar-comision-op" data-opid="${r.opId}" style="${btnStyle("#dc2626",null,true)}" title="Reversar comisión de esta OP">↩ Reversar</button>`
                    : `<button data-action="aplicar-comision-op" data-opid="${r.opId}" style="${btnStyle(amber,null,true)}">✅ Aplicar</button>`}
                </td>
              </tr>`;
            }).join("")}
            <tr style="border-top:2px solid ${amber};background:${amber}08;">
              <td colspan="4" style="padding:10px;font-weight:800;">TOTALES (${conComision.length} OPs)</td>
              <td style="padding:10px;text-align:right;font-weight:800;">${fmtCOP(conComision.reduce((s,r)=>s+r.ventaNeta,0))}</td>
              <td colspan="2"></td>
              <td style="padding:10px;text-align:right;font-weight:800;color:${amber};">${fmtCOP(conComision.reduce((s,r)=>s+r.comisionMonto,0))}</td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;

  // ── Tabla general de todas las OPs del período ────────────────────────
  const detalle = filas.length === 0 ? `
    <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
      <div style="font-size:40px;margin-bottom:10px;">📋</div>
      <div style="color:${C.muted};font-size:14px;">No hay OPs en el período seleccionado o no cumplen condiciones de comisión</div>
    </div>
  ` : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <div style="padding:12px 16px;border-bottom:1px solid ${C.border};font-weight:700;font-size:12px;color:${C.text};">📋 Resumen por OP — Todas las órdenes del período</div>
      <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
        <thead><tr style="background:#f1f5f9;">
          <th style="padding:8px 10px;text-align:left;font-size:10.5px;font-weight:700;">OP</th>
          <th style="padding:8px 10px;text-align:left;font-size:10.5px;font-weight:700;">FECHA</th>
          <th style="padding:8px 10px;text-align:left;font-size:10.5px;font-weight:700;">CLIENTE</th>
          <th style="padding:8px 10px;text-align:left;font-size:10.5px;font-weight:700;">VENDEDOR</th>
          <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;">VENTA NETA</th>
          <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;">MARGEN %</th>
          <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;">COMISIÓN %</th>
          <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:${amber};">COMISIÓN $</th>
        </tr></thead>
        <tbody>
          ${filas.map(r => {
            const margenColor = margenColorFn(r.margen);
            return `
            <tr style="border-top:1px solid ${C.border};">
              <td style="padding:8px 10px;font-weight:700;color:${C.mathiz};">${escapeHTML(r.opNumero)}</td>
              <td style="padding:8px 10px;">${fmtDate(r.fecha)}</td>
              <td style="padding:8px 10px;">${escapeHTML(r.cliente ? r.cliente.nombre : "—")}</td>
              <td style="padding:8px 10px;">
                ${escapeHTML(r.asesorNombre)}
                ${r.vendedor && r.rangoVendedor !== "general" ? `<span style="margin-left:4px;background:${amber}20;color:${amber};padding:1px 6px;border-radius:6px;font-size:10px;">${r.rangoVendedor}</span>` : ""}
              </td>
              <td style="padding:8px 10px;text-align:right;">${fmtCOP(r.ventaNeta)}</td>
              <td style="padding:8px 10px;text-align:right;font-weight:700;color:${margenColor};">${r.margen.toFixed(2)}%</td>
              <td style="padding:8px 10px;text-align:right;">${r.comisionPct > 0 ? r.comisionPct + "%" : "<span style='color:"+C.muted+";'>Sin comisión</span>"}</td>
              <td style="padding:8px 10px;text-align:right;font-weight:800;color:${r.comisionMonto > 0 ? amber : C.muted};">${r.comisionMonto > 0 ? fmtCOP(r.comisionMonto) : "—"}</td>
            </tr>`;
          }).join("")}
          <tr style="border-top:2px solid ${amber};background:${amber}08;">
            <td colspan="4" style="padding:10px;font-weight:800;font-size:12px;">TOTAL</td>
            <td style="padding:10px;text-align:right;font-weight:800;">${fmtCOP(totVenta)}</td>
            <td colspan="2"></td>
            <td style="padding:10px;text-align:right;font-weight:800;color:${amber};font-size:14px;">${fmtCOP(totComision)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  `;

  return filtros + kpis + resumen + borrador + detalle;
}

export function renderTablaComisionesEditor(tabla) {
  const rangos = tabla.rangosVendedor || [];
  const reglas = tabla.reglas || [];
  const vendedores = getVendedores();
  const amber = C.amber;

  // ── Sección de Rangos de Vendedor ────────────────────────────────────
  const rangosSection = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <h3 style="margin:0;font-size:12px;font-weight:700;color:${C.text};">📁 Rangos de Vendedor</h3>
        <button data-action="comisiones-add-rango" style="${btnStyle(amber,null,true)}">+ Agregar rango</button>
      </div>
      ${rangos.length === 0 ? `<div style="color:${C.muted};font-size:12px;text-align:center;padding:11px;">Sin rangos definidos</div>` :
        rangos.map((r, i) => `
          <div style="display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-top:1px solid ${C.border}30;">
            <div style="flex:1;">
              <label style="font-size:10px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">ID</label>
              <input data-comision-rango="${i}" data-field="id" value="${escapeHTML(r.id||"")}" style="${inputStyle}width:100%;font-family:monospace;font-size:11.5px;" placeholder="id_rango"/>
            </div>
            <div style="flex:2;">
              <label style="font-size:10px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">Nombre</label>
              <input data-comision-rango="${i}" data-field="label" value="${escapeHTML(r.label||"")}" style="${inputStyle}width:100%;" placeholder="Nombre del rango"/>
            </div>
            <div style="flex:3;">
              <label style="font-size:10px;font-weight:600;color:${C.muted};display:block;margin-bottom:3px;">Descripción</label>
              <input data-comision-rango="${i}" data-field="descripcion" value="${escapeHTML(r.descripcion||"")}" style="${inputStyle}width:100%;" placeholder="Descripción del rango"/>
            </div>
            <button data-action="comisiones-del-rango" data-idx="${i}" style="${btnStyle("#dc2626",null,true)};margin-top:18px;" title="Eliminar rango">🗑</button>
          </div>
        `).join("")}
    </div>
  `;

  // ── Sección de Reglas de Comisión ─────────────────────────────────────
  const reglasSection = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <h3 style="margin:0;font-size:12px;font-weight:700;color:${C.text};">📐 Reglas de Comisión</h3>
        <button data-action="comisiones-add-regla" style="${btnStyle(amber,null,true)}">+ Agregar regla</button>
      </div>
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
          <thead><tr style="background:#f8fafc;">
            <th style="padding:8px;text-align:left;font-size:10.5px;font-weight:700;">RANGO</th>
            <th style="padding:8px;text-align:left;font-size:10.5px;font-weight:700;">ETIQUETA</th>
            <th style="padding:8px;text-align:right;font-size:10.5px;font-weight:700;">MARGEN MÍN %</th>
            <th style="padding:8px;text-align:right;font-size:10.5px;font-weight:700;">MARGEN MÁX %</th>
            <th style="padding:8px;text-align:right;font-size:10.5px;font-weight:700;color:${amber};">COMISIÓN %</th>
            <th style="padding:8px;"></th>
          </tr></thead>
          <tbody>
            ${reglas.length === 0 ? `<tr><td colspan="6" style="padding:11px;text-align:center;color:${C.muted};">Sin reglas. Agrega una para comenzar.</td></tr>` :
              reglas.map((r, i) => `
              <tr style="border-top:1px solid ${C.border}30;">
                <td style="padding:6px;">
                  <select data-comision-regla="${i}" data-field="rangoVendedor" style="${inputStyle}min-width:100px;">
                    <option value="general" ${r.rangoVendedor==="general"?"selected":""}>General</option>
                    ${rangos.filter(rg=>rg.id!=="general").map(rg=>`<option value="${escapeHTML(rg.id)}" ${r.rangoVendedor===rg.id?"selected":""}>${escapeHTML(rg.label)}</option>`).join("")}
                  </select>
                </td>
                <td style="padding:6px;">
                  <input data-comision-regla="${i}" data-field="label" value="${escapeHTML(r.label||"")}" style="${inputStyle}min-width:150px;" placeholder="Etiqueta"/>
                </td>
                <td style="padding:6px;">
                  <input type="number" step="0.01" data-comision-regla="${i}" data-field="margenMin" value="${r.margenMin??""}" style="${inputStyle}width:80px;text-align:right;" placeholder="0"/>
                </td>
                <td style="padding:6px;">
                  <input type="number" step="0.01" data-comision-regla="${i}" data-field="margenMax" value="${r.margenMax??""}" style="${inputStyle}width:80px;text-align:right;" placeholder="∞"/>
                </td>
                <td style="padding:6px;">
                  <input type="number" step="0.01" data-comision-regla="${i}" data-field="comisionPct" value="${r.comisionPct??""}" style="${inputStyle}width:70px;text-align:right;font-weight:700;color:${amber};" placeholder="0"/>
                </td>
                <td style="padding:6px;">
                  <button data-action="comisiones-del-regla" data-idx="${i}" style="${btnStyle("#dc2626",null,true)}" title="Eliminar">🗑</button>
                </td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;

  // ── Sección de Asignación a Vendedores ───────────────────────────────
  const vendedoresSection = vendedores.length === 0 ? `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <div style="color:${C.muted};font-size:12px;">Sin vendedores registrados. Crea vendedores en el módulo de Maestros.</div>
    </div>
  ` : `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:10px;">
      <h3 style="margin:0 0 9px;font-size:12px;font-weight:700;color:${C.text};">👥 Rango por Vendedor</h3>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:9px;">
        ${vendedores.map(v => `
          <div style="padding:9px;border:1px solid ${C.border};border-radius:6px;">
            <div style="font-size:12px;font-weight:600;margin-bottom:6px;">${escapeHTML(v.nombre)}</div>
            <select data-action="comisiones-asignar-rango" data-vendedor="${v.id}" style="${inputStyle}width:100%;">
              ${rangos.map(rg=>`<option value="${escapeHTML(rg.id)}" ${(v.rango||"general")===rg.id?"selected":""}>${escapeHTML(rg.label)}</option>`).join("")}
            </select>
          </div>
        `).join("")}
      </div>
    </div>
  `;

  return `
    <div>
      <div style="display:flex;justify-content:flex-end;gap:10px;margin-bottom:10px;">
        <button data-action="comisiones-reset-tabla" style="${btnStyle(null,"secondary")}">↩ Restaurar valores por defecto</button>
        <button data-action="comisiones-guardar-tabla" style="${btnStyle(amber)}">💾 Guardar tabla</button>
      </div>
      ${rangosSection}
      ${reglasSection}
      ${vendedoresSection}
    </div>
  `;
}

export function attachComisionesListeners() {
  // Los inputs de comisión-rango y comisión-regla se leen al guardar (comisiones-guardar-tabla),
  // por lo que no necesitan listeners de tiempo real.
  // Las asignaciones de rango a vendedor usan event delegation (data-action="comisiones-asignar-rango")
  // que ya está cubierto por attachListeners() global.
  // Aquí solo garantizamos cualquier listener extra específico del módulo.
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "comisiones-tab": (d, el, e) => {state.comisionesTab = d.tab;
      render();
      return;},
  "comisiones-limpiar-filtros": (d, el, e) => {state.comisionesFiltro = { vendedor:"", desde:"", hasta:"" };
      render();
      return;},
  "comisiones-guardar-tabla": (d, el, e) => {{
      const tabla = getTablaComisiones();
      document.querySelectorAll("[data-comision-rango]").forEach(el => {
        const idx = parseInt(el.dataset.comisionRango);
        const field = el.dataset.field;
        if (tabla.rangosVendedor[idx]) tabla.rangosVendedor[idx][field] = el.value;
      });
      document.querySelectorAll("[data-comision-regla]").forEach(el => {
        const idx = parseInt(el.dataset.comisionRegla);
        const field = el.dataset.field;
        if (tabla.reglas[idx]) {
          const val = ["margenMin","margenMax","comisionPct"].includes(field)
            ? (el.value === "" ? null : parseFloat(el.value))
            : el.value;
          tabla.reglas[idx][field] = val;
        }
      });
      saveTablaComisiones(tabla);
      alert("✅ Tabla de comisiones guardada");
      render();
      return;
    }},
  "comisiones-reset-tabla": (d, el, e) => {if (confirm("¿Restaurar la tabla de comisiones a los valores por defecto?")) {
        saveTablaComisiones(JSON.parse(JSON.stringify(DEFAULT_TABLA_COMISIONES)));
        render();
      }
      return;},
  "comisiones-add-rango": (d, el, e) => {{
      const tablaR = getTablaComisiones();
      tablaR.rangosVendedor.push({ id:"nuevo_"+genId().slice(0,4), label:"Nuevo rango", descripcion:"" });
      saveTablaComisiones(tablaR);
      render();
      return;
    }},
  "comisiones-del-rango": (d, el, e) => {{
      const tablaR = getTablaComisiones();
      const idx = parseInt(d.idx);
      if (confirm("¿Eliminar este rango?")) {
        tablaR.rangosVendedor.splice(idx, 1);
        saveTablaComisiones(tablaR);
        render();
      }
      return;
    }},
  "comisiones-add-regla": (d, el, e) => {{
      const tablaReg = getTablaComisiones();
      tablaReg.reglas.push({ id:genId(), rangoVendedor:"general", label:"Nueva regla", margenMin:0, margenMax:null, comisionPct:0 });
      saveTablaComisiones(tablaReg);
      render();
      return;
    }},
  "comisiones-del-regla": (d, el, e) => {{
      const tablaReg = getTablaComisiones();
      const idx = parseInt(d.idx);
      if (confirm("¿Eliminar esta regla?")) {
        tablaReg.reglas.splice(idx, 1);
        saveTablaComisiones(tablaReg);
        render();
      }
      return;
    }},
  "comisiones-asignar-rango": (d, el, e) => {{
      const vid = d.vendedor;
      const empleadosArr = state.data.empleados || [];
      const vend = empleadosArr.find(x => x.id === vid);
      if (vend) {
        vend.rango = e.target ? e.target.value : d.rango;
        saveData("empleados", empleadosArr);
      }
      return;
    }},
  "aplicar-comision-op": (d, el, e) => {{
      const opIdCom = d.opid;
      const pedidosArr = state.data.pedidos || [];
      const opObj = pedidosArr.find(p => p.id === opIdCom);
      if (!opObj) return;
      // Recalcular comisión para esta OP
      const vendedoresArr2 = getVendedores();
      const liqOp = getLiquidacionOP(opIdCom) || { ventaTotal: parseNumber(opObj.total)||0, margen: 0, costoTotal: 0 };
      const vendOpObj = vendedoresArr2.find(v => v.nombre === (opObj.asesor_comercial || ""));
      const rangoOp = vendOpObj ? (vendOpObj.rango || "general") : "general";
      const pctOp = calcComisionPct(liqOp.margen || 0, rangoOp);
      const montoOp = Math.round((liqOp.ventaTotal || 0) * (pctOp / 100));
      if (montoOp <= 0) { alert("Esta OP no genera comisión calculable."); return; }
      const asesorNombreOp = opObj.asesor_comercial || "Sin asignar";
      const margenPost = liqOp.ventaTotal > 0
        ? (((liqOp.ventaTotal - (liqOp.costoTotal||0) - montoOp) / liqOp.ventaTotal) * 100).toFixed(2)
        : "0.00";
      if (!confirm(
        `¿Aplicar comisión de ${fmtCOP(montoOp)} (${asesorNombreOp}) a la liquidación de costos de ${opObj.numero||opIdCom}?\n\n` +
        `• Costo total actual: ${fmtCOP(liqOp.costoTotal||0)}\n` +
        `• Margen actual: ${(liqOp.margen||0).toFixed(2)}%\n` +
        `• Nuevo costo total: ${fmtCOP((liqOp.costoTotal||0) + montoOp)}\n` +
        `• Nuevo margen estimado: ${margenPost}%\n\n` +
        `Esta acción agregará la comisión como concepto de costo en la OP.`
      )) return;
      // Actualizar _conceptosCosto: quitar entrada anterior (_esComision) si existe, agregar nueva
      const conceptosActuales = (opObj._conceptosCosto || []).filter(c => !c._esComision);
      conceptosActuales.push({
        concepto: `Comisión: ${asesorNombreOp}`,
        proveedor_nombre: "",
        cantidad: 1,
        valor_tercero: montoOp,
        _esComision: true,
        _fecha: today(),
        _vendedor: asesorNombreOp
      });
      const opActualizada = { ...opObj, _conceptosCosto: conceptosActuales };
      saveData("pedidos", pedidosArr.map(p => p.id === opIdCom ? opActualizada : p));
      render();
      return;
    }},
  "aplicar-todas-comisiones": (d, el, e) => {{
      const f2 = state.comisionesFiltro || {};
      const filasTodas = liquidarComisiones(f2.desde, f2.hasta, f2.vendedor);
      const pendientesTodas = filasTodas.filter(r => r.comisionMonto > 0 && !r.yaAplicada);
      if (pendientesTodas.length === 0) { alert("No hay comisiones pendientes de aplicar."); return; }
      const totMonto = pendientesTodas.reduce((s, r) => s + r.comisionMonto, 0);
      if (!confirm(
        `¿Aplicar ${pendientesTodas.length} comisiones pendientes a sus respectivas OPs?\n\n` +
        `Total a imputar como costo: ${fmtCOP(totMonto)}\n\n` +
        `Esta acción actualizará la liquidación de costos de cada OP seleccionada.`
      )) return;
      const pedidosMut = [...(state.data.pedidos || [])];
      pendientesTodas.forEach(r => {
        const idx = pedidosMut.findIndex(p => p.id === r.opId);
        if (idx === -1) return;
        const op = pedidosMut[idx];
        const conceptosFilt = (op._conceptosCosto || []).filter(c => !c._esComision);
        conceptosFilt.push({
          concepto: `Comisión: ${r.asesorNombre}`,
          proveedor_nombre: "",
          cantidad: 1,
          valor_tercero: r.comisionMonto,
          _esComision: true,
          _fecha: today(),
          _vendedor: r.asesorNombre
        });
        pedidosMut[idx] = { ...op, _conceptosCosto: conceptosFilt };
      });
      saveData("pedidos", pedidosMut);
      render();
      return;
    }},
  "reversar-comision-op": (d, el, e) => {{
      const opIdRev = d.opid;
      const pedidosRev = state.data.pedidos || [];
      const opRev = pedidosRev.find(p => p.id === opIdRev);
      if (!opRev) return;
      const entradaRev = getComisionEntradaOP(opRev);
      if (!entradaRev) return;
      if (!confirm(
        `¿Reversar la comisión de ${fmtCOP(parseNumber(entradaRev.valor_tercero)||0)} aplicada a la OP ${opRev.numero||opIdRev}?\n\n` +
        `Esta acción eliminará el concepto de comisión de la liquidación de costos de la OP.`
      )) return;
      const conceptosRev = (opRev._conceptosCosto || []).filter(c => !c._esComision);
      saveData("pedidos", pedidosRev.map(p => p.id === opIdRev ? { ...opRev, _conceptosCosto: conceptosRev } : p));
      render();
      return;
    }},
});
